import { Colors, Figures } from "../config";

// Standard Chess Piece Values for evaluation
const PIECE_VALUES = {
  [Figures.PAWN]: 100,
  [Figures.KNIGHT]: 320,
  [Figures.BISHOP]: 330,
  [Figures.ROOK]: 500,
  [Figures.QUEEN]: 900,
  [Figures.KING]: 20000,
};

// Positional tables to guide AI play style (e.g. piece activity, center control)
// Coordinates in the game are 1-indexed (1 to 8)
const PAWN_PST_WHITE = [
  [0,  0,  0,  0,  0,  0,  0,  0], // Row 8 (unused, index 7)
  [50, 50, 50, 50, 50, 50, 50, 50], // Row 7
  [10, 10, 20, 30, 30, 20, 10, 10], // Row 6
  [5,  5, 10, 25, 25, 10,  5,  5], // Row 5
  [0,  0,  0, 20, 20,  0,  0,  0], // Row 4
  [5, -5,-10,  0,  0,-10, -5,  5], // Row 3
  [5, 10, 10,-20,-20, 10, 10,  5], // Row 2
  [0,  0,  0,  0,  0,  0,  0,  0]  // Row 1
];

const PAWN_PST_BLACK = [...PAWN_PST_WHITE].reverse();

const KNIGHT_PST = [
  [-50,-40,-30,-30,-30,-30,-40,-50],
  [-40,-20,  0,  0,  0,  0,-20,-40],
  [-30,  0, 10, 15, 15, 10,  0,-30],
  [-30,  5, 15, 20, 20, 15,  5,-30],
  [-30,  0, 15, 20, 20, 15,  0,-30],
  [-30,  5, 10, 15, 15, 10,  5,-30],
  [-40,-20,  0,  5,  5,  0,-20,-40],
  [-50,-40,-30,-30,-30,-30,-40,-50]
];

const BISHOP_PST = [
  [-20,-10,-10,-10,-10,-10,-10,-20],
  [-10,  0,  0,  0,  0,  0,  0,-10],
  [-10,  0,  5, 10, 10,  5,  0,-10],
  [-10,  5,  5, 10, 10,  5,  5,-10],
  [-10,  0, 10, 10, 10, 10,  0,-10],
  [-10, 10, 10, 10, 10, 10, 10,-10],
  [-10,  5,  0,  0,  0,  0,  5,-10],
  [-20,-10,-10,-10,-10,-10,-10,-20]
];

const ROOK_PST = [
  [ 0,  0,  0,  0,  0,  0,  0,  0],
  [ 5, 10, 10, 10, 10, 10, 10,  5],
  [-5,  0,  0,  0,  0,  0,  0, -5],
  [-5,  0,  0,  0,  0,  0,  0, -5],
  [-5,  0,  0,  0,  0,  0,  0, -5],
  [-5,  0,  0,  0,  0,  0,  0, -5],
  [-5,  0,  0,  0,  0,  0,  0, -5],
  [ 0,  0,  0,  5,  5,  0,  0,  0]
];

const QUEEN_PST = [
  [-20,-10,-10, -5, -5,-10,-10,-20],
  [-10,  0,  0,  0,  0,  0,  0,-10],
  [-10,  0,  5,  5,  5,  5,  0,-10],
  [ -5,  0,  5,  5,  5,  5,  0, -5],
  [  0,  0,  5,  5,  5,  5,  0, -5],
  [-10,  5,  5,  5,  5,  5,  0,-10],
  [-10,  0,  5,  0,  0,  5,  0,-10],
  [-20,-10,-10, -5, -5,-10,-10,-20]
];

const KING_MIDDLE_GAME_PST = [
  [-30,-40,-40,-50,-50,-40,-40,-30],
  [-30,-40,-40,-50,-50,-40,-40,-30],
  [-30,-40,-40,-50,-50,-40,-40,-30],
  [-30,-40,-40,-50,-50,-40,-40,-30],
  [-20,-30,-30,-40,-40,-30,-30,-20],
  [-10,-20,-20,-20,-20,-20,-20,-10],
  [ 20, 20,  0,  0,  0,  0, 20, 20],
  [ 20, 30, 10,  0,  0, 10, 30, 20]
];

// Helper to get positional bonus for a piece based on x, y coordinates
const getPositionalBonus = (name, color, x, y) => {
  const colIdx = x - 1;
  const rowIdx = 8 - y; // map y=8 to row 0, y=1 to row 7
  if (rowIdx < 0 || rowIdx > 7 || colIdx < 0 || colIdx > 7) return 0;

  switch (name) {
    case Figures.PAWN:
      return color === Colors.WHITE ? PAWN_PST_WHITE[rowIdx][colIdx] : PAWN_PST_BLACK[rowIdx][colIdx];
    case Figures.KNIGHT:
      return KNIGHT_PST[rowIdx][colIdx];
    case Figures.BISHOP:
      return BISHOP_PST[rowIdx][colIdx];
    case Figures.ROOK:
      return ROOK_PST[rowIdx][colIdx];
    case Figures.QUEEN:
      return QUEEN_PST[rowIdx][colIdx];
    case Figures.KING:
      return KING_MIDDLE_GAME_PST[rowIdx][colIdx];
    default:
      return 0;
  }
};

// Simulated Move Generator replicating Board.jsx rules
// Optimized: Accepts pre-built lookup to avoid recreating objects thousands of times
export const getAvailableCellsSimulated = (figure, cellsFigure, dangerousCellsOfOpponent = {}) => {
  let way = [];

  const toStopWay = (x, y) => {
    if (x < 1 || x > 8 || y < 1 || y > 8) return true; // Out of bounds
    if (cellsFigure[`${x}-${y}`]) return true; // Blocked by piece
    return false;
  };

  const checkCellForMove = (x, y) => {
    if (toStopWay(x, y)) return false;
    way.push({ x, y });
    return true;
  };

  const verticalTop = (toY, fromY = figure.y) => {
    for (let i = fromY + 1; i <= toY; i++) {
      if (toStopWay(figure.x, i)) return;
      way.push({ y: i, x: figure.x });
    }
  };

  const verticalBottom = (toY, fromY = figure.y) => {
    for (let i = fromY - 1; i >= toY; i--) {
      if (toStopWay(figure.x, i)) return;
      way.push({ y: i, x: figure.x });
    }
  };

  const horizontalLeft = (toX, fromX = figure.x) => {
    for (let i = fromX - 1; i >= toX; i--) {
      if (toStopWay(i, figure.y)) return;
      way.push({ x: i, y: figure.y });
    }
  };

  const horizontalRight = (toX, fromX = figure.x) => {
    for (let i = fromX + 1; i <= toX; i++) {
      if (toStopWay(i, figure.y)) return;
      way.push({ x: i, y: figure.y });
    }
  };

  const checkDiagonal = () => {
    for (let i = 1; i <= 8; i++) {
      if (!checkCellForMove(figure.x + i, figure.y + i)) break;
    }
    for (let i = 1; i <= 8; i++) {
      if (!checkCellForMove(figure.x + i, figure.y - i)) break;
    }
    for (let i = 1; i <= 8; i++) {
      if (!checkCellForMove(figure.x - i, figure.y - i)) break;
    }
    for (let i = 1; i <= 8; i++) {
      if (!checkCellForMove(figure.x - i, figure.y + i)) break;
    }
  };

  const isEatableCell = (x, y) => {
    if (
      cellsFigure[`${x}-${y}`] &&
      figure.color !== cellsFigure[`${x}-${y}`]?.color
    )
      return true;
    return false;
  };

  const checkEatableCell = (x, y) => {
    if (isEatableCell(x, y)) {
      way.push({ x, y });
      return true;
    }
    return false;
  };

  const checkEatableOrAlliesCell = (x, y) => {
    if (
      cellsFigure[`${x}-${y}`] &&
      cellsFigure[`${x}-${y}`]?.color === figure.color
    )
      return true;
    if (isEatableCell(x, y)) {
      way.push({ x, y });
      return true;
    }
    return false;
  };

  const checkEatableFiguresByDiagonal = () => {
    for (let i = 1; i <= 8; i++) {
      if (checkEatableOrAlliesCell(figure.x + i, figure.y + i)) break;
    }
    for (let i = 1; i <= 8; i++) {
      if (checkEatableOrAlliesCell(figure.x + i, figure.y - i)) break;
    }
    for (let i = 1; i <= 8; i++) {
      if (checkEatableOrAlliesCell(figure.x - i, figure.y - i)) break;
    }
    for (let i = 1; i <= 8; i++) {
      if (checkEatableOrAlliesCell(figure.x - i, figure.y + i)) break;
    }
  };

  const checkEatableFiguresByRook = () => {
    for (let i = figure.y + 1; i <= 8; i++) {
      if (checkEatableOrAlliesCell(figure.x, i)) break;
    }
    for (let i = figure.y - 1; i >= 0; i--) {
      if (checkEatableOrAlliesCell(figure.x, i)) break;
    }
    for (let i = figure.x - 1; i >= 0; i--) {
      if (checkEatableOrAlliesCell(i, figure.y)) break;
    }
    for (let i = figure.x + 1; i <= 8; i++) {
      if (checkEatableOrAlliesCell(i, figure.y)) break;
    }
  };

  // PAWN
  if (figure.name === Figures.PAWN) {
    if (figure.color === Colors.BLACK) {
      if (dangerousCellsOfOpponent === true) {
        // Just controlled cells
        way.push({ y: figure.y - 1, x: figure.x - 1 });
        way.push({ y: figure.y - 1, x: figure.x + 1 });
      } else {
        verticalBottom(figure.y - 2);
        checkEatableCell(figure.x - 1, figure.y - 1);
        checkEatableCell(figure.x + 1, figure.y - 1);
      }
    } else {
      if (dangerousCellsOfOpponent === true) {
        // Just controlled cells
        way.push({ y: figure.y + 1, x: figure.x - 1 });
        way.push({ y: figure.y + 1, x: figure.x + 1 });
      } else {
        verticalTop(figure.y + 2);
        checkEatableCell(figure.x - 1, figure.y + 1);
        checkEatableCell(figure.x + 1, figure.y + 1);
      }
    }
  }

  // ROOK
  else if (figure.name === Figures.ROOK) {
    verticalBottom(0);
    verticalTop(8);
    horizontalLeft(0);
    horizontalRight(8);
    checkEatableFiguresByRook();
  }

  // KNIGHT
  else if (figure.name === Figures.KNIGHT) {
    const checkMovesByKnight = () => {
      checkCellForMove(figure.x + 1, figure.y + 2);
      checkCellForMove(figure.x - 1, figure.y + 2);
      checkCellForMove(figure.x + 2, figure.y + 1);
      checkCellForMove(figure.x + 2, figure.y - 1);
      checkCellForMove(figure.x + 1, figure.y - 2);
      checkCellForMove(figure.x - 1, figure.y - 2);
      checkCellForMove(figure.x - 2, figure.y - 1);
      checkCellForMove(figure.x - 2, figure.y + 1);
    };

    const checkEatableFiguresByKnight = () => {
      checkEatableOrAlliesCell(figure.x + 1, figure.y + 2);
      checkEatableOrAlliesCell(figure.x - 1, figure.y + 2);
      checkEatableOrAlliesCell(figure.x + 2, figure.y + 1);
      checkEatableOrAlliesCell(figure.x + 2, figure.y - 1);
      checkEatableOrAlliesCell(figure.x + 1, figure.y - 2);
      checkEatableOrAlliesCell(figure.x - 1, figure.y - 2);
      checkEatableOrAlliesCell(figure.x - 2, figure.y - 1);
      checkEatableOrAlliesCell(figure.x - 2, figure.y + 1);
    };

    checkMovesByKnight();
    checkEatableFiguresByKnight();
  }

  // BISHOP
  else if (figure.name === Figures.BISHOP) {
    checkDiagonal();
    checkEatableFiguresByDiagonal();
  }

  // QUEEN
  else if (figure.name === Figures.QUEEN) {
    checkDiagonal();
    checkEatableFiguresByDiagonal();
    verticalBottom(0);
    verticalTop(8);
    horizontalLeft(0);
    horizontalRight(8);
    checkEatableFiguresByRook();
  }

  // KING
  else if (figure.name === Figures.KING) {
    const checkKingDiagonal = () => {
      checkCellForMove(figure.x + 1, figure.y + 1);
      checkCellForMove(figure.x + 1, figure.y - 1);
      checkCellForMove(figure.x - 1, figure.y - 1);
      checkCellForMove(figure.x - 1, figure.y + 1);
    };

    const checkEatableFiguresByKing = () => {
      checkEatableOrAlliesCell(figure.x + 1, figure.y + 1);
      checkEatableOrAlliesCell(figure.x + 1, figure.y - 1);
      checkEatableOrAlliesCell(figure.x - 1, figure.y - 1);
      checkEatableOrAlliesCell(figure.x - 1, figure.y + 1);
      checkEatableOrAlliesCell(figure.x + 1, figure.y);
      checkEatableOrAlliesCell(figure.x - 1, figure.y);
      checkEatableOrAlliesCell(figure.x, figure.y + 1);
      checkEatableOrAlliesCell(figure.x, figure.y - 1);
    };

    verticalBottom(figure.y - 1);
    verticalTop(figure.y + 1);
    horizontalLeft(figure.x - 1);
    horizontalRight(figure.x + 1);
    checkKingDiagonal();
    checkEatableFiguresByKing();

    // Remove moves leading to dangerous opponent cells
    if (dangerousCellsOfOpponent && dangerousCellsOfOpponent !== true) {
      const cellsForRemoving = [];
      for (let i = 0; i < way.length; i++) {
        if (dangerousCellsOfOpponent[`${way[i].x}-${way[i].y}`]) {
          cellsForRemoving.push({ x: way[i].x, y: way[i].y });
        }
      }
      cellsForRemoving.forEach((elw) => {
        way = way.filter((el) => !(el.y === elw.y && el.x === elw.x));
      });
    }
  }

  // Map array to object keys representation
  const obj = {};
  way.forEach((el) => {
    // Only accept valid board coordinates
    if (el.x >= 1 && el.x <= 8 && el.y >= 1 && el.y <= 8) {
      obj[`${el.x}-${el.y}`] = true;
    }
  });
  return obj;
};

// Get all dangerous cells controlled by a side
export const getDangerousCellsSimulated = (figuresState, color, cellsFigure) => {
  let dangerous = {};
  const sideFigures = Object.values(figuresState).filter((f) => f.color === color);
  
  sideFigures.forEach((figure) => {
    dangerous = {
      ...dangerous,
      ...getAvailableCellsSimulated(figure, cellsFigure, true),
    };
  });
  return dangerous;
};

// Generate all valid moves for a player
export const getAllMoves = (figuresState, color) => {
  const moves = [];
  const sideFigures = Object.values(figuresState).filter((f) => f.color === color);
  const otherColor = color === Colors.WHITE ? Colors.BLACK : Colors.WHITE;
  
  // Construct lookup once for performance (huge optimization: from O(N^2) to O(N))
  const cellsFigure = {};
  for (const id in figuresState) {
    const f = figuresState[id];
    cellsFigure[`${f.x}-${f.y}`] = f;
  }
  
  // Calculate dangerous cells using the pre-built lookup
  const opponentDangerous = getDangerousCellsSimulated(figuresState, otherColor, cellsFigure);

  sideFigures.forEach((figure) => {
    const available = getAvailableCellsSimulated(figure, cellsFigure, opponentDangerous);
    for (const cell of Object.keys(available)) {
      const [tx, ty] = cell.split("-").map(Number);
      moves.push({
        figure,
        toX: tx,
        toY: ty,
      });
    }
  });

  return moves;
};

// Simulates executing a move on the board state
export const makeSimulatedMove = (figuresState, move) => {
  const nextFigures = {};
  
  // Deep clone figures state
  for (const id in figuresState) {
    nextFigures[id] = { ...figuresState[id] };
  }

  // Find if there is a piece at the destination and delete it
  const targetId = Object.keys(nextFigures).find(
    (id) => nextFigures[id].x === move.toX && nextFigures[id].y === move.toY
  );
  if (targetId) {
    delete nextFigures[targetId];
  }

  // Apply the movement
  if (nextFigures[move.figure.id]) {
    nextFigures[move.figure.id].x = move.toX;
    nextFigures[move.figure.id].y = move.toY;
  }

  return nextFigures;
};

// Evaluate the board from the perspective of AI Color
const evaluateBoard = (figuresState, aiColor) => {
  let score = 0;
  
  for (const id in figuresState) {
    const f = figuresState[id];
    const materialValue = PIECE_VALUES[f.name] || 0;
    const positionalValue = getPositionalBonus(f.name, f.color, f.x, f.y);
    
    const value = materialValue + positionalValue;
    
    if (f.color === aiColor) {
      score += value;
    } else {
      score -= value;
    }
  }
  
  return score;
};

// Minimax with Alpha-Beta Pruning
const minimax = (figuresState, depth, alpha, beta, isMaximizing, activeColor, aiColor) => {
  // Check if King of either side is captured
  const whiteKing = Object.values(figuresState).find((f) => f.name === Figures.KING && f.color === Colors.WHITE);
  const blackKing = Object.values(figuresState).find((f) => f.name === Figures.KING && f.color === Colors.BLACK);
  
  if (!whiteKing) {
    return aiColor === Colors.BLACK ? 19999 + depth : -19999 - depth;
  }
  if (!blackKing) {
    return aiColor === Colors.WHITE ? 19999 + depth : -19999 - depth;
  }
  
  if (depth === 0) {
    return evaluateBoard(figuresState, aiColor);
  }
  
  const otherColor = activeColor === Colors.WHITE ? Colors.BLACK : Colors.WHITE;
  const moves = getAllMoves(figuresState, activeColor);
  
  if (moves.length === 0) {
    return evaluateBoard(figuresState, aiColor);
  }
  
  // Sort moves to optimize Alpha-Beta pruning (heuristics: prioritize captures)
  moves.sort((a, b) => {
    const targetA = Object.values(figuresState).find((f) => f.x === a.toX && f.y === a.toY);
    const targetB = Object.values(figuresState).find((f) => f.x === b.toX && f.y === b.toY);
    const scoreA = targetA ? PIECE_VALUES[targetA.name] : 0;
    const scoreB = targetB ? PIECE_VALUES[targetB.name] : 0;
    return scoreB - scoreA;
  });

  if (isMaximizing) {
    let maxEval = -Infinity;
    for (const move of moves) {
      const nextState = makeSimulatedMove(figuresState, move);
      const evaluation = minimax(nextState, depth - 1, alpha, beta, false, otherColor, aiColor);
      maxEval = Math.max(maxEval, evaluation);
      alpha = Math.max(alpha, evaluation);
      if (beta <= alpha) break; // Beta cut-off
    }
    return maxEval;
  } else {
    let minEval = Infinity;
    for (const move of moves) {
      const nextState = makeSimulatedMove(figuresState, move);
      const evaluation = minimax(nextState, depth - 1, alpha, beta, true, otherColor, aiColor);
      minEval = Math.min(minEval, evaluation);
      beta = Math.min(beta, evaluation);
      if (beta <= alpha) break; // Alpha cut-off
    }
    return minEval;
  }
};

// Main entry point for the Chess AI with difficulty level selection
export const getBestMove = (figuresState, aiColor, difficulty = "hard") => {
  const moves = getAllMoves(figuresState, aiColor);
  if (moves.length === 0) return null;
  
  // reguler -> depth 2, hard -> depth 3, hell -> depth 4
  const depth = difficulty === "reguler" ? 2 : difficulty === "hell" ? 4 : 3;
  
  let bestMove = null;
  let bestValue = -Infinity;
  const otherColor = aiColor === Colors.WHITE ? Colors.BLACK : Colors.WHITE;
  
  // Sort moves for pruning optimization
  moves.sort((a, b) => {
    const targetA = Object.values(figuresState).find((f) => f.x === a.toX && f.y === a.toY);
    const targetB = Object.values(figuresState).find((f) => f.x === b.toX && f.y === b.toY);
    const scoreA = targetA ? PIECE_VALUES[targetA.name] : 0;
    const scoreB = targetB ? PIECE_VALUES[targetB.name] : 0;
    return scoreB - scoreA;
  });

  for (const move of moves) {
    const nextState = makeSimulatedMove(figuresState, move);
    const boardValue = minimax(nextState, depth - 1, -Infinity, Infinity, false, otherColor, aiColor);
    
    if (boardValue > bestValue) {
      bestValue = boardValue;
      bestMove = move;
    }
  }
  
  if (!bestMove && moves.length > 0) {
    return moves[0];
  }
  
  return bestMove;
};
